import React, { useState, useEffect } from "react";
import "./Signup.css";
import { useNavigate } from "react-router-dom";

const STEPS = ["Personal", "Guardian", "Course", "Security"];

function Signup() {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);

  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", dob: "", gender: "",
    aadharNumber: "", education: "",
    fatherName: "", motherName: "", guardianPhone: "",
    branchId: "", course: "", modeOfTraining: "",
    password: "", confirmPassword: "",
    address: { street: "", city: "", state: "", pincode: "" }
  });

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchCourses(formData.branchId);
    // Reset course when branch changes
    setFormData(prev => ({ ...prev, course: "" }));
  }, [formData.branchId]);

  const fetchBranches = async () => {
    try {
      const res = await fetch("http://localhost:5000/api/student-auth/branches");
      const data = await res.json();
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) { setBranches([]); }
  };

  const fetchCourses = async (branchId = "") => {
    try {
      const url = branchId 
        ? `http://localhost:5000/api/student-auth/courses?branchId=${branchId}`
        : `http://localhost:5000/api/student-auth/courses`;
      const res = await fetch(url);
      const data = await res.json();
      setCourses(Array.isArray(data) ? data : []);
    } catch (err) { setCourses([]); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData({ ...formData, [parent]: { ...formData[parent], [child]: value } });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setProfileImage(file);
    if (file) setProfilePreview(URL.createObjectURL(file));
  };

  const validateStep = () => {
    setErrorMsg("");
    if (currentStep === 0) {
      if (!formData.name || !formData.email || !formData.phone) {
        setErrorMsg("Please fill in Name, Email, and Phone.");
        return false;
      }
    }
    if (currentStep === 1) {
      if (!formData.guardianPhone) {
        setErrorMsg("Guardian phone is required.");
        return false;
      }
    }
    if (currentStep === 2) {
      if (!formData.branchId || !formData.course) {
        setErrorMsg("Please select a Branch and Course.");
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const prevStep = () => { setErrorMsg(""); setCurrentStep((s) => Math.max(s - 1, 0)); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (formData.password !== formData.confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }
    if (formData.password.length < 6) {
      setErrorMsg("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      const submitData = { ...formData };
      delete submitData.confirmPassword;
      const fd = new FormData();
      fd.append("data", JSON.stringify(submitData));
      if (profileImage) fd.append("profileImage", profileImage);

      const res = await fetch("http://localhost:5000/api/student-auth/signup", {
        method: "POST", body: fd,
      });
      const result = await res.json();
      if (res.ok) {
        localStorage.setItem("studentToken", result.token);
        localStorage.setItem("studentData", JSON.stringify(result.student));
        navigate("/student-dashboard");
      } else {
        setErrorMsg(result.message || "Registration failed.");
      }
    } catch (err) {
      setErrorMsg("Network error. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="su-wrapper">
      {/* ─── LEFT PANEL ─── */}
      <div className="su-left">
        <div className="su-left-inner">
          <div className="su-logo-box">
            <img src="/webmok-logo.png" alt="Logo"
              onError={(e) => { e.target.style.display = "none"; }} />
            <span className="su-logo-text">WebMok</span>
          </div>

          <h1 className="su-title">Start Your<br />Learning Journey</h1>
          <p className="su-subtitle">
            Join thousands of students already enrolled in our courses. 
            Fill out the quick registration form to get started.
          </p>

          {/* Step Indicators */}
          <div className="su-steps-sidebar">
            {STEPS.map((step, i) => (
              <div key={i} className={`su-step-item ${i === currentStep ? "active" : ""} ${i < currentStep ? "done" : ""}`}>
                <div className="su-step-circle">
                  {i < currentStep ? "✓" : i + 1}
                </div>
                <span>{step}</span>
              </div>
            ))}
          </div>

          {/* Feature bullets */}
          <div className="su-features">
            <div className="su-feature">🎓 Expert-led Courses</div>
            <div className="su-feature">🏢 Multiple Branches</div>
            <div className="su-feature">📊 Track Your Progress</div>
            <div className="su-feature">🔒 100% Secure</div>
          </div>
        </div>
      </div>

      {/* ─── RIGHT PANEL ─── */}
      <div className="su-right">
        <div className="su-form-wrapper">
          <div className="su-form-header">
            <div className="su-step-label">Step {currentStep + 1} of {STEPS.length}</div>
            <h2 className="su-form-title">
              {currentStep === 0 && "Personal Information"}
              {currentStep === 1 && "Guardian Details"}
              {currentStep === 2 && "Course & Branch"}
              {currentStep === 3 && "Create Password"}
            </h2>
            {/* Mobile stepper */}
            <div className="su-mobile-steps">
              {STEPS.map((_, i) => (
                <div key={i} className={`su-mobile-dot ${i === currentStep ? "active" : ""} ${i < currentStep ? "done" : ""}`} />
              ))}
            </div>
          </div>

          {errorMsg && (
            <div className="su-error">
              <span>⚠</span> {errorMsg}
            </div>
          )}

          <form onSubmit={currentStep === STEPS.length - 1 ? handleSubmit : (e) => { e.preventDefault(); nextStep(); }}>

            {/* ── STEP 1: Personal ── */}
            {currentStep === 0 && (
              <div className="su-fields">
                <div className="su-photo-upload">
                  <div className="su-photo-circle" onClick={() => document.getElementById("photoInput").click()}>
                    {profilePreview
                      ? <img src={profilePreview} alt="Preview" />
                      : <div className="su-photo-placeholder"><span>📷</span><p>Upload Photo</p></div>
                    }
                  </div>
                  <input id="photoInput" type="file" accept="image/*" onChange={handleFileChange} hidden />
                  <p className="su-photo-hint">Click to upload profile photo</p>
                </div>

                <div className="su-grid-2">
                  <div className="su-field">
                    <label>Full Name <span>*</span></label>
                    <input type="text" name="name" placeholder="Enter your full name"
                      value={formData.name} onChange={handleChange} required />
                  </div>
                  <div className="su-field">
                    <label>Email Address <span>*</span></label>
                    <input type="email" name="email" placeholder="your@email.com"
                      value={formData.email} onChange={handleChange} required />
                  </div>
                  <div className="su-field">
                    <label>Phone Number <span>*</span></label>
                    <input type="tel" name="phone" placeholder="+91 00000 00000"
                      value={formData.phone} onChange={handleChange} required />
                  </div>
                  <div className="su-field">
                    <label>Date of Birth</label>
                    <input type="date" name="dob" value={formData.dob} onChange={handleChange} />
                  </div>
                  <div className="su-field">
                    <label>Gender</label>
                    <select name="gender" value={formData.gender} onChange={handleChange}>
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="su-field">
                    <label>Aadhar Number</label>
                    <input type="text" name="aadharNumber" placeholder="12-digit Aadhar"
                      value={formData.aadharNumber} onChange={handleChange} />
                  </div>
                  <div className="su-field">
                    <label>Education / Qualification</label>
                    <input type="text" name="education" placeholder="e.g. B.Tech, 12th Pass"
                      value={formData.education} onChange={handleChange} />
                  </div>
                  <div className="su-field">
                    <label>City</label>
                    <input type="text" name="address.city" placeholder="Your city"
                      value={formData.address.city} onChange={handleChange} />
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 2: Guardian ── */}
            {currentStep === 1 && (
              <div className="su-fields">
                <div className="su-grid-2">
                  <div className="su-field">
                    <label>Father's Name</label>
                    <input type="text" name="fatherName" placeholder="Father's full name"
                      value={formData.fatherName} onChange={handleChange} />
                  </div>
                  <div className="su-field">
                    <label>Mother's Name</label>
                    <input type="text" name="motherName" placeholder="Mother's full name"
                      value={formData.motherName} onChange={handleChange} />
                  </div>
                  <div className="su-field su-field-full">
                    <label>Guardian Phone <span>*</span></label>
                    <input type="tel" name="guardianPhone" placeholder="Guardian's mobile number"
                      value={formData.guardianPhone} onChange={handleChange} required />
                  </div>
                  <div className="su-field su-field-full">
                    <label>Street Address</label>
                    <input type="text" name="address.street" placeholder="Street / Area / Colony"
                      value={formData.address.street} onChange={handleChange} />
                  </div>
                  <div className="su-field">
                    <label>State</label>
                    <input type="text" name="address.state" placeholder="State"
                      value={formData.address.state} onChange={handleChange} />
                  </div>
                  <div className="su-field">
                    <label>Pincode</label>
                    <input type="text" name="address.pincode" placeholder="Pincode"
                      value={formData.address.pincode} onChange={handleChange} />
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 3: Course & Branch ── */}
            {currentStep === 2 && (
              <div className="su-fields">
                <div className="su-grid-1">
                  <div className="su-field">
                    <label>Select Branch <span>*</span></label>
                    <select name="branchId" value={formData.branchId} onChange={handleChange} required>
                      <option value="">-- Choose a Branch --</option>
                      {branches?.map(b => (
                        <option key={b._id} value={b._id}>{b.branchName} — {b.city}</option>
                      ))}
                    </select>
                  </div>
                  <div className="su-field">
                    <label>Select Course <span>*</span></label>
                    <select name="course" value={formData.course} onChange={handleChange} required disabled={!formData.branchId}>
                      <option value="">{formData.branchId ? "-- Choose a Course --" : "-- Select Branch First --"}</option>
                      {courses?.map((c, i) => (
                        <option key={i} value={c.courseName}>{c.courseName}</option>
                      ))}
                    </select>
                    {formData.course && courses.find(c => c.courseName === formData.course)?.feeAmount !== undefined && (
                      <div className="su-course-fee-display" style={{ marginTop: '8px', padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>💰</span> Course Fee: ₹{courses.find(c => c.courseName === formData.course)?.feeAmount.toLocaleString('en-IN')}
                      </div>
                    )}
                  </div>
                  <div className="su-field">
                    <label>Mode of Training</label>
                    <div className="su-radio-group">
                      {["Online", "Offline", "Hybrid"].map(m => (
                        <label key={m} className={`su-radio-card ${formData.modeOfTraining === m ? "selected" : ""}`}>
                          <input type="radio" name="modeOfTraining" value={m}
                            checked={formData.modeOfTraining === m} onChange={handleChange} hidden />
                          <span className="su-radio-icon">
                            {m === "Online" ? "💻" : m === "Offline" ? "🏫" : "🔀"}
                          </span>
                          <span>{m}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 4: Password ── */}
            {currentStep === 3 && (
              <div className="su-fields">
                <div className="su-review-box">
                  {profilePreview && <img src={profilePreview} alt="Profile" className="su-review-img" />}
                  <div>
                    <h4>{formData.name}</h4>
                    <p>{formData.email}</p>
                    <p>{formData.course} · {branches.find(b => b._id === formData.branchId)?.branchName}</p>
                  </div>
                </div>

                <div className="su-grid-1">
                  <div className="su-field">
                    <label>Create Password <span>*</span></label>
                    <input type="password" name="password" placeholder="Min. 6 characters"
                      value={formData.password} onChange={handleChange} required minLength="6" />
                  </div>
                  <div className="su-field">
                    <label>Confirm Password <span>*</span></label>
                    <input type="password" name="confirmPassword" placeholder="Re-enter your password"
                      value={formData.confirmPassword} onChange={handleChange} required />
                  </div>
                </div>
              </div>
            )}

            {/* ── Navigation ── */}
            <div className="su-nav-btns">
              {currentStep > 0 && (
                <button type="button" className="su-btn-back" onClick={prevStep}>← Back</button>
              )}
              {currentStep < STEPS.length - 1 ? (
                <button type="submit" className="su-btn-next">Continue →</button>
              ) : (
                <button type="submit" className="su-btn-submit" disabled={loading}>
                  {loading ? <span className="su-spinner"></span> : "🎓 Complete Registration"}
                </button>
              )}
            </div>
          </form>

          <p className="su-login-link">
            Already have an account? <a href="/login">Sign In</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Signup;